import { useCallback, useMemo, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router'
import { Virtuoso } from 'react-virtuoso'
import { z } from 'zod'
import { ButtonLegacy } from '@/components/ui/button-legacy'
import {
	Combobox,
	ComboboxComponents,
	type ComboboxComponentsNamespace,
	ComboboxContent,
	ComboboxInput,
	ComboboxItem
} from '@/components/ui/combobox'
import { DataTable } from '@/components/ui/data-table'
import { ChevronDownIcon, EyeIcon, SearchLgIcon } from '@/components/ui/icons'
import { Input } from '@/components/ui/input'
import { Skeleton } from '@/components/ui/skeleton'
import { Switch } from '@/components/ui/switch'
import { formatDate } from '@/dates'
import { useAuthStore } from '@/features/auth/store/store'
import { EmptyStateListings } from '@/features/luxe-submissions/components/empty-state-listings'
import { FilterLocationSelect } from '@/features/luxe-submissions/components/filter-location-select'
import { FilterVetterSelect } from '@/features/luxe-submissions/components/filter-vetter-select'
import { FinalScoreBadge } from '@/features/luxe-submissions/components/final-score-badge'
import { ReviewerNameLabel } from '@/features/luxe-submissions/components/reviewer-name-label'
import { SourceBadge } from '@/features/luxe-submissions/components/source-badge'
import { SourceFilterSelect } from '@/features/luxe-submissions/components/source-filter-select'
import { StatusFilterSelect } from '@/features/luxe-submissions/components/status-filter-select'
import { SubmissionTypeFilterSelect } from '@/features/luxe-submissions/components/submission-type-filter-select'
import { useAssignListing } from '@/features/luxe-submissions/queries/use-assign-listing'
import { useClaimListing } from '@/features/luxe-submissions/queries/use-claim-listing'
import { useLuxeFeatureFlag } from '@/features/luxe-submissions/queries/use-get-luxe-feature-flags'
import { useGetReviewers } from '@/features/luxe-submissions/queries/use-get-reviewers'
import { useGetVisualVettingListings } from '@/features/luxe-submissions/queries/use-get-visual-vetting-listings'
import {
	SubmissionSource,
	SubmissionStatus,
	SubmissionType,
	type Vetter,
	type VisualVettingListing
} from '@/features/luxe-submissions/types'
import {
	FULL_ACCESS_USER_ROLES,
	submissionStatusToBadgeStyle,
	submissionStatusToLabelTKey,
	submissionTypeToLabelTKey,
	toArrayParam
} from '@/features/luxe-submissions/utils'
import type { Order, OrderDirection } from '@/helpers/types'
import { cn, debounce, errorHandler } from '@/helpers/utils'
import { useParsedSearchParams } from '@/hooks/use-parsed-search-params'
import {
	ROW_SELECTION_COLUMN_ID,
	useRowSelection
} from '@/hooks/use-row-selection'
import { useRowsPerPage } from '@/hooks/use-rows-per-page'
import { routes } from '@/routes'
import { useTranslation } from '@/translations/i18n'

export const VisualVettingSearchParamsSchema = z.object({
	sortBy: z.string().optional(),
	sortOrder: z.enum(['asc', 'desc']).optional(),
	locationId: z.string().optional(),
	vetterId: z.string().optional(),
	source: SubmissionSource.optional(),
	submissionType: SubmissionType.optional(),
	status: SubmissionStatus.optional(),
	search: z.string().optional(),
	page: z.string().optional(),
	perPage: z.string().optional(),
	assignedToMe: z.enum(['true']).optional(),
	showRejected: z.enum(['true']).optional(),
	// Pre-check only, but parsed here because the queue layout reads this one
	// schema to keep every tab count in step with the list it sits above.
	unassigned: z.enum(['true']).optional(),
	showOnlyToReview: z.enum(['true']).optional()
})

type SortColumn =
	| 'listingId'
	| 'location'
	| 'submissionType'
	| 'source'
	| 'vetter'
	| 'status'

function LuxeSubmissionsQueueVisualVetting() {
	const { t } = useTranslation()
	const navigate = useNavigate()
	const location = useLocation()
	const user = useAuthStore((state) => state.user)
	const isAdmin = user?.role
		? FULL_ACCESS_USER_ROLES.includes(user.role)
		: false
	const isLuxeOps = user?.role === 'luxe_ops'
	const { searchParams, setSearchParams } = useParsedSearchParams(
		VisualVettingSearchParamsSchema
	)
	const currentPage = searchParams.page ? Number(searchParams.page) : 1
	const { isEnabled: isUnassignedFilterEnabled } = useLuxeFeatureFlag(
		'luxe-vv2-unassigned-filter'
	)
	const { isEnabled: isFinalScoreEnabled } = useLuxeFeatureFlag(
		'luxe-vv2-final-score-display'
	)
	// Only completed submissions have a score, so the column would be all
	// dashes under any other filter
	const isCompletedFilter = searchParams.status === 'completed'

	const { rowsPerPage, setRowsPerPage, totalRows, reportTotal } =
		useRowsPerPage({
			searchParam: searchParams.perPage,
			setSearchParam: (newPerPage) =>
				setSearchParams({ perPage: newPerPage, page: undefined })
		})

	// Queries
	const { data: listingsData, isLoading: isLoadingListings } =
		useGetVisualVettingListings({
			page: currentPage,
			perPage: rowsPerPage,
			sortBy: searchParams.sortBy,
			sortOrder: searchParams.sortOrder,
			locations: toArrayParam(searchParams.locationId),
			vetterIds: toArrayParam(searchParams.vetterId),
			sources: toArrayParam(searchParams.source),
			submissionTypes: toArrayParam(searchParams.submissionType),
			statuses: toArrayParam(searchParams.status),
			search: searchParams.search,
			assignedToMe:
				searchParams.assignedToMe === 'true' ? true : undefined,
			unassigned:
				isUnassignedFilterEnabled && searchParams.unassigned === 'true'
					? true
					: undefined
		})

	// Mutations
	const claimMutation = useClaimListing()
	const assignMutation = useAssignListing()

	// Row selection
	const { rowSelectionColumn, selectionCount, resetSelection, selected } =
		useRowSelection<VisualVettingListing, 'id'>({
			idField: 'id',
			data: listingsData?.data,
			//user can't select completed listings and listings assigned to others,
			//but admins can reassign listings so they can select all except completed
			isSelectable: (listing) =>
				listing.status !== 'completed' && (!listing.vetter || isAdmin),
			getDisabledReason: (listing) => {
				if (listing.status === 'completed') {
					return t(
						'luxe_submissions:completed_select_disabled_reason'
					)
				}
				if (listing.vetter && !isAdmin) {
					return t(
						'luxe_submissions:assigned_to_other_vetter_select_disabled_reason'
					)
				}
				return undefined
			}
		})

	// Pagination
	const lastPage = listingsData
		? Math.ceil(listingsData.meta.total / listingsData.meta.perPage)
		: undefined
	reportTotal(listingsData?.meta.total)

	const setPage = (page: number) => {
		setSearchParams({
			page: page === 1 ? undefined : String(page)
		})
	}

	// Filters
	const handleLocationChange = (locationId: string | null) => {
		setSearchParams({
			locationId: locationId ?? undefined,
			page: undefined
		})
	}

	const handleVetterChange = (vetterId: string | null) => {
		setSearchParams({
			vetterId: vetterId ?? undefined,
			page: undefined
		})
	}

	const handleSourceChange = (source: string | null) => {
		setSearchParams({
			source: (source as SubmissionSource) ?? undefined,
			page: undefined
		})
	}

	const handleSubmissionTypeChange = (submissionType: string | null) => {
		setSearchParams({
			submissionType: (submissionType as SubmissionType) ?? undefined,
			page: undefined
		})
	}

	const handleStatusChange = (status: string | null) => {
		setSearchParams({
			...(status === 'completed' ? { unassigned: undefined } : {}),
			page: undefined,
			status: (status as SubmissionStatus | null) ?? undefined
		})
	}

	const handleUnassignedChange = (checked: boolean) => {
		setSearchParams({
			unassigned: checked ? 'true' : undefined,
			page: undefined
		})
	}

	// Debounced search by ID and email
	const debouncedSearch = useMemo(
		() =>
			debounce((value: string) => {
				setSearchParams({
					search: value || undefined,
					page: undefined
				})
			}, 300),
		[setSearchParams]
	)

	// Navigate to listing
	const handleViewListing = useCallback(
		(listingId: string) => {
			navigate(
				routes.LUXE_SUBMISSIONS_LISTING_SCORE_FORM.handle.fullPath.replace(
					':id',
					listingId
				),
				{ state: { fromSearch: location.search } }
			)
		},
		[navigate, location.search]
	)

	// Claim listing
	const handleClaim = useCallback(
		async (listing: VisualVettingListing) => {
			try {
				await claimMutation.mutateAsync([listing.id])
				handleViewListing(listing.id)
			} catch (error) {
				errorHandler(error)
			}
		},
		[claimMutation, handleViewListing]
	)
	// Bulk claim
	const handleBulkClaim = useCallback(async () => {
		try {
			await claimMutation.mutateAsync(selected)
			resetSelection()
		} catch (error) {
			errorHandler(error)
		}
	}, [selected, claimMutation, resetSelection])

	// Assign (admins + ops) / reassign (admins only) listing
	const handleAssign = useCallback(
		async (listingId: string, reviewerId: number) => {
			try {
				await assignMutation.mutateAsync({
					listingIds: [listingId],
					reviewerId
				})
			} catch (error) {
				errorHandler(error)
			}
		},
		[assignMutation]
	)

	// Bulk assign handler
	const handleBulkAssign = useCallback(
		async (reviewerId: number) => {
			try {
				await assignMutation.mutateAsync({
					listingIds: selected,
					reviewerId
				})
				resetSelection()
			} catch (error) {
				errorHandler(error)
			}
		},
		[selected, assignMutation, resetSelection]
	)

	// Order state for DataTable
	const order = useMemo((): Order<SortColumn> | undefined => {
		if (!searchParams.sortBy) {
			return undefined
		}
		return {
			orderBy: searchParams.sortBy as SortColumn,
			orderDirection: searchParams.sortOrder as OrderDirection | undefined
		}
	}, [searchParams.sortBy, searchParams.sortOrder])

	const setOrder = (newOrder?: Order<SortColumn>) => {
		setSearchParams({
			sortBy: newOrder?.orderBy,
			sortOrder: newOrder?.orderDirection,
			page: undefined
		})
	}
	const hasFilters = Object.values(searchParams).some(Boolean)

	// Table columns
	const columns = useMemo(
		() => [
			rowSelectionColumn,
			{
				id: 'listingId' as const,
				header: t('luxe_submissions:listing_id_column'),
				cell: ({ dataItem }: { dataItem: VisualVettingListing }) => (
					<a
						className={cn(
							'truncate font-medium text-neutral-900 text-sm',
							dataItem.listingUrl && 'underline'
						)}
						href={dataItem.listingUrl ?? undefined}
						rel='noopener noreferrer'
						target='_blank'
						title={dataItem.airbnbListingId ?? dataItem.id}
					>
						#{dataItem.airbnbListingId ?? dataItem.id}
					</a>
				),
				size: 240,
				sortKey: 'listingId' as const
			},
			{
				id: 'location' as const,
				header: t('luxe_submissions:location_column'),
				cell: ({ dataItem }: { dataItem: VisualVettingListing }) => {
					return dataItem.location ? (
						<span className='text-neutral-700 text-sm'>
							{dataItem.location}
						</span>
					) : (
						<span className='text-neutral-500 text-sm'>—</span>
					)
				},
				size: 160,
				sortKey: 'location' as const
			},
			{
				id: 'submissionType' as const,
				header: t('luxe_submissions:submission_type_column'),
				cell: ({ dataItem }: { dataItem: VisualVettingListing }) =>
					dataItem.submissionType ? (
						<span className='text-neutral-700 text-sm'>
							{t(
								submissionTypeToLabelTKey[
									dataItem.submissionType
								]
							)}
						</span>
					) : (
						<span className='text-neutral-500 text-sm'>—</span>
					),
				size: 210,
				sortKey: 'submissionType' as const
			},
			{
				id: 'submittedAt' as const,
				header: t('luxe_submissions:submitted_at_column'),
				cell: ({ dataItem }: { dataItem: VisualVettingListing }) => {
					return dataItem.queueEnteredAt === null ? (
						<span className='text-neutral-500 text-sm'>—</span>
					) : (
						<span className='text-neutral-700 text-sm'>
							{formatDate(dataItem.queueEnteredAt, 'PP')}
						</span>
					)
				},
				size: 165
			},
			{
				id: 'source' as const,
				header: t('luxe_submissions:source_column'),
				cell: ({ dataItem }: { dataItem: VisualVettingListing }) => (
					<SourceBadge source={dataItem.source} />
				),
				size: 160,
				sortKey: 'source' as const
			},
			{
				id: 'status' as const,
				header: t('luxe_submissions:status_column'),
				cell: ({ dataItem }: { dataItem: VisualVettingListing }) => (
					<span
						className={`inline-flex rounded-full border px-2 py-0.5 font-medium text-xs ${submissionStatusToBadgeStyle[dataItem.status]}`}
					>
						{t(submissionStatusToLabelTKey[dataItem.status])}
					</span>
				),
				size: 160,
				sortKey: 'status' as const
			},
			...(isCompletedFilter && isFinalScoreEnabled
				? [
						{
							id: 'score' as const,
							header: t('luxe_submissions:final_score_column'),
							cell: ({
								dataItem
							}: {
								dataItem: VisualVettingListing
							}) => (
								<FinalScoreBadge
									blockersFailedCount={
										dataItem.blockersFailedCount
									}
									designScore={dataItem.score}
									finalScore={dataItem.finalScore}
								/>
							),
							size: 160
						}
					]
				: []),
			{
				id: 'vetter' as const,
				header: t('luxe_submissions:vetter_column'),
				cell: ({ dataItem }: { dataItem: VisualVettingListing }) => {
					if (dataItem.status === 'skipped') {
						return
					}
					// If already assigned and not admin, show the vetter name
					if (
						dataItem.vetter &&
						(!isAdmin || dataItem.status === 'completed')
					) {
						return (
							<ReviewerNameLabel
								{...dataItem.vetter}
								isMe={
									String(dataItem.vetter?.id) ===
									String(user?.id)
								}
							/>
						)
					}

					// Admins and ops see assign dropdown
					if (isAdmin || isLuxeOps) {
						return (
							<VetterSelect
								onSelect={(reviewerId) =>
									handleAssign(dataItem.id, reviewerId)
								}
								selectedVetter={dataItem.vetter ?? undefined}
								userId={user?.id}
								variant='cell'
							/>
						)
					}

					// Non-admin sees claim button
					return (
						<ButtonLegacy
							className='px-0'
							disabled={claimMutation.isPending}
							onClick={() => handleClaim(dataItem)}
							size='sm'
							type='button'
							variant='tertiary'
						>
							{t('luxe_submissions:claim')}
						</ButtonLegacy>
					)
				},
				size: 240
			},
			{
				id: 'action' as const,
				header: '',
				cell: ({ dataItem }: { dataItem: VisualVettingListing }) => {
					if (dataItem.status === 'skipped') {
						return
					}

					return (
						<button
							className='flex cursor-pointer items-center justify-center text-neutral-500 hover:text-neutral-700'
							onClick={() => handleViewListing(dataItem.id)}
							type='button'
						>
							<EyeIcon size={20} />
						</button>
					)
				},
				size: 60
			}
		],
		[
			t,
			isAdmin,
			isLuxeOps,
			isCompletedFilter,
			isFinalScoreEnabled,
			handleAssign,
			handleClaim,
			handleViewListing,
			claimMutation.isPending,
			rowSelectionColumn,
			user
		]
	)

	return (
		<div className='flex flex-1 flex-col overflow-hidden px-10'>
			{/* Filters */}
			<div className='flex shrink-0 items-center justify-between gap-3 py-6'>
				<div className='flex min-w-0 items-center gap-3'>
					<FilterLocationSelect
						onChange={handleLocationChange}
						selectedId={searchParams.locationId ?? null}
					/>

					<SubmissionTypeFilterSelect
						onChange={handleSubmissionTypeChange}
						value={searchParams.submissionType ?? null}
					/>

					<SourceFilterSelect
						onChange={handleSourceChange}
						value={searchParams.source ?? null}
					/>

					<StatusFilterSelect
						onChange={handleStatusChange}
						value={searchParams.status ?? null}
					/>

					<FilterVetterSelect
						onChange={handleVetterChange}
						section='visual_vetting'
						selectedId={searchParams.vetterId ?? null}
						userId={user?.id ?? ''}
					/>

					{isUnassignedFilterEnabled && (
						<div className='flex items-center gap-2'>
							<Switch
								checked={searchParams.unassigned === 'true'}
								onChange={handleUnassignedChange}
							/>
							<span className='font-medium text-neutral-700 text-sm'>
								{t('luxe_submissions:unassigned_only')}
							</span>
						</div>
					)}
				</div>
				<div className='relative w-[320px]'>
					<SearchLgIcon
						className='pointer-events-none absolute top-1/2 left-3 -translate-y-1/2 text-neutral-500'
						size={20}
					/>
					<Input
						className='h-9 w-full rounded-lg border-neutral-300 pl-10 text-sm shadow-xs placeholder:text-neutral-500'
						defaultValue={searchParams.search ?? ''}
						onChange={(e) => debouncedSearch(e.target.value)}
						placeholder={t(
							'luxe_submissions:search_by_listing_id_email'
						)}
					/>
				</div>
			</div>

			{/* Selection bar */}
			{selectionCount > 0 && (
				<div className='mb-4 flex items-center justify-between rounded-lg border border-neutral-200 bg-primary-50 px-6 py-3'>
					<span className='font-medium text-neutral-600 text-xs'>
						{selectionCount} Selected
					</span>
					<div className='flex items-center gap-6'>
						{isAdmin || isLuxeOps ? (
							<VetterSelect
								onSelect={handleBulkAssign}
								userId={user?.id}
								variant='bulk'
							/>
						) : (
							<ButtonLegacy
								disabled={claimMutation.isPending}
								onClick={handleBulkClaim}
								size='sm'
								type='button'
								variant='outline'
							>
								{t('luxe_submissions:claim')}
							</ButtonLegacy>
						)}
						<button
							className='cursor-pointer font-medium text-neutral-900 text-sm underline'
							onClick={resetSelection}
							type='button'
						>
							{t('common:clear')}
						</button>
					</div>
				</div>
			)}

			{/* Table */}
			<div className='flex-1 overflow-hidden'>
				<DataTable
					columnPinning={{
						left: [ROW_SELECTION_COLUMN_ID],
						right: ['action']
					}}
					columns={columns}
					data={listingsData?.data ?? []}
					emptyComponent={
						<VisualVettingEmptyState hasFilters={hasFilters} />
					}
					isLoading={isLoadingListings}
					lastPage={lastPage}
					order={order}
					page={currentPage}
					rowsPerPage={rowsPerPage}
					setOrder={setOrder}
					setPage={setPage}
					setRowsPerPage={setRowsPerPage}
					totalRows={totalRows}
				/>
			</div>
		</div>
	)
}

// Empty state - generic copy when filters are applied, queue copy otherwise
function VisualVettingEmptyState({ hasFilters }: { hasFilters: boolean }) {
	const { t } = useTranslation()

	return (
		<EmptyStateListings
			action={
				hasFilters ? null : (
					<ButtonLegacy
						onClick={() => window.location.reload()}
						variant='secondary'
					>
						{t('common:refresh')}
					</ButtonLegacy>
				)
			}
			description={t(
				hasFilters
					? 'common:empty_table_description'
					: 'luxe_submissions:visual_vetting_no_listings_description'
			)}
			title={t(
				hasFilters
					? 'common:empty_table_title'
					: 'luxe_submissions:visual_vetting_no_listings_title'
			)}
		/>
	)
}

// Vetter select component - used in table cells and bulk assign
interface VetterSelectProps {
	onSelect?: (vetterId: number) => void
	variant: 'cell' | 'bulk'
	selectedVetter?: Vetter
	userId?: string
}

function VetterSelect({
	onSelect,
	variant,
	selectedVetter,
	userId
}: VetterSelectProps) {
	const { t } = useTranslation()
	const [reviewerSearch, setReviewerSearch] = useState('')
	const {
		remappedData: reviewers,
		isLoading,
		isFetchingNextPage,
		hasNextPage,
		fetchNextPage
	} = useGetReviewers('visual_vetting', reviewerSearch)

	const lastSearchRef = useRef('')
	const debouncedSearch = useMemo(
		() =>
			debounce((val: string) => {
				// Only trigger search if value changed to avoid unnecessary re-renders
				if (val !== lastSearchRef.current) {
					lastSearchRef.current = val
					setReviewerSearch(val)
				}
			}, 300),
		[]
	)

	const handleValueChange = (
		vetterId: number | null,
		_eventDetails: ComboboxComponentsNamespace.Root.ChangeEventDetails
	) => {
		if (vetterId) {
			onSelect?.(vetterId)
		}
	}

	const renderContent = () => {
		if (isLoading) {
			return (
				<div className='flex flex-col gap-1 p-1'>
					<Skeleton height={36} width='100%' />
					<Skeleton height={36} width='100%' />
					<Skeleton height={36} width='100%' />
				</div>
			)
		}

		if (reviewers.length === 0) {
			return (
				<div className='p-2 text-center text-neutral-500 text-sm'>
					No reviewers found
				</div>
			)
		}

		return (
			<Virtuoso
				className='h-50! [&::-webkit-scrollbar-thumb:hover]:bg-neutral-400 [&::-webkit-scrollbar-thumb]:rounded-[3px] [&::-webkit-scrollbar-thumb]:bg-neutral-300 [&::-webkit-scrollbar]:w-1 [&::-webkit-scrollbar]:bg-neutral-50'
				computeItemKey={(_, item) => item.id}
				data={reviewers}
				endReached={() => {
					if (hasNextPage && !isFetchingNextPage) {
						fetchNextPage()
					}
				}}
				itemContent={(_, reviewer) => {
					return (
						<ComboboxItem value={reviewer.id}>
							<ReviewerNameLabel
								{...reviewer}
								isMe={String(reviewer.id) === String(userId)}
							/>
						</ComboboxItem>
					)
				}}
			/>
		)
	}

	const popoverWidth = variant === 'cell' ? 'w-[200px]' : 'w-[320px]'

	return (
		<Combobox
			modal
			onInputValueChange={(inputValue: string) =>
				debouncedSearch(inputValue)
			}
			onValueChange={handleValueChange}
			value={null}
		>
			{variant === 'cell' ? (
				<ComboboxComponents.Trigger
					className={cn(
						'flex h-10 cursor-pointer items-center justify-between gap-2 rounded-lg border border-neutral-300 bg-neutral-white px-3.5 text-base text-neutral-500 shadow-xs',
						'data-popup-open:shadow-focus-primary-100-xxs data-popup-open:outline data-popup-open:outline-neutral-300',
						popoverWidth
					)}
				>
					{selectedVetter ? (
						<ReviewerNameLabel {...selectedVetter} />
					) : (
						t('luxe_submissions:assign_task')
					)}
					<ComboboxComponents.Icon className='text-neutral-500'>
						<ChevronDownIcon
							className='in-data-popup-open:rotate-180 transition-transform duration-150'
							size={16}
						/>
					</ComboboxComponents.Icon>
				</ComboboxComponents.Trigger>
			) : (
				<ComboboxComponents.Trigger
					className={cn(
						'flex h-10 cursor-pointer items-center gap-2 rounded-lg border border-neutral-300 bg-neutral-white px-3.5 text-base text-neutral-500 shadow-xs',
						'data-popup-open:shadow-focus-primary-100-xxs data-popup-open:outline data-popup-open:outline-neutral-300',
						popoverWidth
					)}
				>
					Select vetter to assign task
					<ComboboxComponents.Icon className='ml-auto text-neutral-500'>
						<ChevronDownIcon
							className='in-data-popup-open:rotate-180 transition-transform duration-150'
							size={16}
						/>
					</ComboboxComponents.Icon>
				</ComboboxComponents.Trigger>
			)}
			<ComboboxContent className={popoverWidth}>
				<ComboboxInput placeholder={t('luxe_submissions:search')} />
				<div className='max-h-50 overflow-hidden'>
					{renderContent()}
					{isFetchingNextPage && (
						<div className='p-1'>
							<Skeleton height={36} width='100%' />
						</div>
					)}
				</div>
			</ComboboxContent>
		</Combobox>
	)
}

export { LuxeSubmissionsQueueVisualVetting }
